import { createAction, Action } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

// Panel visibility + a one-shot prompt handed off from the dashboard hero.
// The conversation itself lives in the AssistantWidget component (mounted once
// at the app root so history survives minimise + route changes).
export interface AssistantState {
  isOpen: boolean;
  isMinimized: boolean;
  /** A prompt to send as soon as the panel opens (consumed by the widget). */
  pendingPrompt: string | null;
}

export const OPEN = 'assistant/OPEN';
export const MINIMIZE = 'assistant/MINIMIZE';
export const CLOSE = 'assistant/CLOSE';
export const CLEAR_PENDING = 'assistant/CLEAR_PENDING';

export const initialState: AssistantState = {
  isOpen: false,
  isMinimized: false,
  pendingPrompt: null,
};

/** Open the panel, optionally seeding a first prompt to send. */
export const openAssistant = createAction<string | undefined>(OPEN);
/** Collapse the panel to the floating launcher (keeps history). */
export const minimizeAssistant = createAction(MINIMIZE);
/** Close the panel entirely (history is kept and restorable). */
export const closeAssistant = createAction(CLOSE);
/** Mark the pending prompt as consumed. */
export const clearPendingPrompt = createAction(CLEAR_PENDING);

export const assistantReducer = (
  state: AssistantState = initialState,
  action: Action,
): AssistantState => {
  if (openAssistant.match(action)) {
    return {
      isOpen: true,
      isMinimized: false,
      pendingPrompt: action.payload ?? state.pendingPrompt,
    };
  }
  if (minimizeAssistant.match(action)) {
    return { ...state, isMinimized: true };
  }
  if (closeAssistant.match(action)) {
    return { ...state, isOpen: false, isMinimized: false };
  }
  if (clearPendingPrompt.match(action)) {
    return { ...state, pendingPrompt: null };
  }
  return state;
};

export const useAssistantSelector = () =>
  useSelector<{ assistant: AssistantState }, AssistantState>(
    (state) => state.assistant,
  );
